import { GetGroupRowAggParams } from 'ag-grid-community';
import { get, isNumber, set } from 'lodash';
import { TableOverviewRow } from 'src/domain';
import { safeRound } from 'src/utils/math';
import { formatMonthFieldToMonthKey } from 'src/utils/monthKeys';

export function weightedAverage<T>(
    items: T[],
    fields: string[],
    weightFn: (item: T, field: string) => number | [number, number | undefined]
): Partial<T> {
    // biome-ignore lint/suspicious/noExplicitAny: Method returns Partial<T> but can include nested keys, so we need any
    const result: any = {};

    for (const field of fields) {
        set(result, field, 0);

        for (const item of items) {
            processWeightedData<T>(item, field, weightFn, result);
        }

        processFieldResult(result, field);
    }
    return result;
}

function processWeightedData<T>(
    item: T,
    field: string,
    weightFn: (item: T, field: string) => number | [number, number | undefined],
    // biome-ignore lint/suspicious/noExplicitAny: result can be of any type
    result: any
) {
    const value = get(item, field);
    const weight = weightFn(item, field);

    if (isNumber(value) && isNumber(weight)) {
        set(result, field, (get(result, field) as number) + weight * value);
    }

    if (Array.isArray(value)) {
        if (!Array.isArray(result[field])) {
            set(
                result,
                field,
                Array.from({ length: value.length }, () => undefined)
            );
        }

        const currentResult = get(result, field) as [number, number | undefined];

        const actualWeight = Array.isArray(weight) ? weight[0] : weight;
        const expectedWeight = Array.isArray(weight) ? (weight[1] ?? weight[0]) : weight;
        const actualValue = Array.isArray(value) ? value[0] : value;
        const expectedValue = Array.isArray(value) ? (value[1] ?? value[0]) : value;

        if (typeof currentResult[0] !== 'number') {
            currentResult[0] = 0;
        }
        currentResult[0] += (actualWeight ?? 0) * actualValue;

        if (typeof currentResult[1] !== 'number') {
            currentResult[1] = 0;
        }
        currentResult[1] += expectedWeight * expectedValue;
        set(result, field, currentResult);
    }
}

// biome-ignore lint/suspicious/noExplicitAny: result can be of any type
function processFieldResult(result: any, field: string) {
    let fieldValue = get(result, field);
    if (Array.isArray(fieldValue) && fieldValue[1] === fieldValue[0]) {
        fieldValue[1] = undefined;
    }

    if (Array.isArray(fieldValue)) {
        fieldValue[0] = safeRound(fieldValue[0]);
        fieldValue[1] = safeRound(fieldValue[1]);
    } else {
        fieldValue = safeRound(fieldValue);
    }

    set(result, field, fieldValue);
}

export function sumAverage<T>(items: T[], fields: string[]): Partial<T> {
    // biome-ignore lint/suspicious/noExplicitAny: Method returns Partial<T> but can include nested keys, so we need any
    const result: any = {};

    for (const field of fields) {
        set(result, field, 0);

        for (const item of items) {
            const value = get(item, field);

            if (isNumber(value)) {
                set(result, field, (get(result, field) as number) + value);
            }

            if (Array.isArray(value)) {
                if (!Array.isArray(result[field])) {
                    set(
                        result,
                        field,
                        Array.from({ length: value.length }, () => 0)
                    );
                }

                const actualValue = Array.isArray(value) ? value[0] : value;
                const expectedValue = Array.isArray(value) ? (value[1] ?? value[0]) : value;

                const current = get(result, field);
                current[0] += actualValue;
                current[1] += expectedValue;
                set(result, field, current);
            }
        }

        processFieldResult(result, field);
    }

    return result;
}

export type Aggregation = TableOverviewRow['columns'];
export const calculateAveragesWithPlannedSplits = (params: GetGroupRowAggParams<TableOverviewRow>): Aggregation => {
    const items = (params.nodes.map((n) => n.data).filter(Boolean) as TableOverviewRow[]).map((row) => row.columns);
    const fields = Object.keys(items[0] ?? {}).filter((f) => !f.endsWith('Id') && !f.endsWith('PlannedSplit'));
    const result = weightedAverage(items, fields, (item, field) => (item?.[`${field}PlannedSplit`] ?? 0) / 100);
    return result as Record<string, number>;
};

export function calculateWeightedAverages(dataArray: { columns: Record<string, number> }[]): Aggregation {
    const items = dataArray.map((row) => row.columns);
    const fields = Object.keys(dataArray[0]?.columns || {}).filter(
        (f) => !f.endsWith('Id') && !f.endsWith('PlannedSplit')
    );

    const weightedAvgs = weightedAverage(items, fields, (item, field) => (item?.[`${field}PlannedSplit`] ?? 0) / 100);

    const results = Object.fromEntries(
        Object.entries(weightedAvgs).map(([fieldName, value]) => [formatMonthFieldToMonthKey(fieldName), value])
    );

    return results;
}

export function calculateMeanMonthlyAverages(dataArray: { columns: Record<string, number> }[]): Record<number, number> {
    const totals = dataArray.reduce<Record<number, { sum: number; count: number }>>((acc, item) => {
        Object.entries(item.columns).forEach(([fieldName, value]) => {
            if (fieldName.endsWith('Id')) return;

            const monthKey = formatMonthFieldToMonthKey(fieldName);
            if (!monthKey) return;

            if (!acc[monthKey]) {
                acc[monthKey] = { sum: 0, count: 0 };
            }

            if (isNumber(value)) {
                acc[monthKey].sum += value;
                acc[monthKey].count += 1;
            }
        });
        return acc;
    }, {});

    const averages: Record<number, number> = {};
    Object.entries(totals).forEach(([monthKeyStr, { sum, count }]) => {
        const monthKey = Number.parseInt(monthKeyStr, 10);
        averages[monthKey] = count === 0 ? 0 : sum / count;
    });

    return averages;
}
